import React, { useState, useEffect } from 'react';
import { Shield, Brain, Zap, ArrowRight, Search, Settings, Database } from 'lucide-react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate

const OverviewPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [particles, setParticles] = useState([]);
  const [scrollY, setScrollY] = useState(0);

  const navigate = useNavigate(); // Initialize useNavigate

  useEffect(() => {
    setIsVisible(true);

    // Generate floating particles
    const newParticles = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 5 + 2,
      speed: Math.random() * 3 + 1,
      opacity: Math.random() * 0.6 + 0.2,
      color: Math.random() > 0.5 ? 'blue' : 'purple',
    }));
    setParticles(newParticles);

    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  const handleNavigation = (path) => {
    navigate(path); // Use navigate for redirection
  };

  const products = [
    {
      name: 'BIQUA',
      description: 'Our next-gen cybersecurity shield powered by Blockchain, AI, and Quantum Computing.',
      icon: Shield,
      color: 'blue',
      features: ['Quantum Encryption', 'AI Threat Detection', 'Blockchain Security', 'Real-time Monitoring']
    },
    {
      name: 'QUBISA',
      description: 'A quantum-intelligent assistant designed to think ahead, adapt, and respond in real time.',
      icon: Brain,
      color: 'purple',
      features: ['Quantum Processing', 'Predictive Intelligence', 'Real-time Adaptation', 'Advanced Analytics']
    },
    {
      name: 'G-FLEA',
      description: 'A high-speed learning engine enabling smarter AI development and rapid quantum model testing.',
      icon: Zap,
      color: 'cyan',
      features: ['Rapid Learning', 'Quantum Models', 'AI Development', 'Performance Testing']
    }
  ];

  const actionButtons = [
    {
      title: 'Overview List',
      description: 'Comprehensive system overview and dashboard',
      path: '/Overview-List',
      icon: Database,
      color: 'blue'
    },
    {
      title: 'Scan Interface',
      description: 'Advanced scanning and analysis tools',
      path: '/scan',
      icon: Search,
      color: 'purple'
    },
    {
      title: 'Deploy & Exploit',
      description: 'Deployment and exploitation interface',
      path: '/deploy',
      icon: Settings,
      color: 'cyan'
    },
    {
      title: 'Data Exfiltration',
      description: 'Data extraction and analysis tools',
      path: '/exfiltration',
      icon: Database,
      color: 'pink'
    }
  ];

  return (
    <div
      className="relative min-h-screen bg-white overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      {/* Dynamic Background Mesh */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(168, 85, 247, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 40% 60%, rgba(14, 165, 233, 0.08) 0%, transparent 50%)
            `,
            animation: 'meshMove 15s ease-in-out infinite',
          }}
        />
      </div>

      {/* Animated Grid */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, #3b82f6 1px, transparent 1px),
              linear-gradient(to bottom, #3b82f6 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            animation: 'gridPulse 8s ease-in-out infinite',
            transform: `translateY(${scrollY * 0.1}px)`,
          }}
        />
      </div>

      {/* Enhanced Floating Particles */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className={`absolute rounded-full ${
            particle.color === 'blue' ? 'bg-blue-400' : 'bg-purple-400'
          } shadow-lg`}
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            opacity: particle.opacity * 0.3,
            animation: `particleFloat ${particle.speed * 4 + 6}s ease-in-out infinite`,
            animationDelay: `${particle.id * 0.05}s`,
            pointerEvents: 'none',
            boxShadow: `0 0 ${particle.size * 2}px ${
              particle.color === 'blue' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(168, 85, 247, 0.3)'
            }`,
          }}
        />
      ))}

      {/* Interactive Gradient Orb */}
      <div
        className="absolute inset-0 pointer-events-none transition-all duration-300"
        style={{
          background: `radial-gradient(800px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(59, 130, 246, 0.08), rgba(168, 85, 247, 0.05) 40%, transparent 70%)`,
        }}
      />

      {/* Main Content - Reduced top padding and margins */}
      <div className="relative z-10 px-6 pt-8 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Header Section - Reduced margin */}
          <div className="text-center mb-8">
            <div
              className={`inline-flex items-center gap-3 px-6 py-3 rounded-full border border-blue-300 bg-gradient-to-r from-blue-100 to-purple-100 mb-4 transform transition-all duration-1000 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
            >
              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 animate-pulse shadow-lg" />
              <span className="text-sm font-semibold text-blue-700 tracking-wide uppercase">
                Innovation Hub Overview
              </span>
            </div>

            <h1
              className={`text-4xl md:text-6xl lg:text-7xl font-black leading-none tracking-tight mb-4 transform transition-all duration-1000 delay-200 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
            >
              <span className="block bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
                GQBS LAB
              </span>
              <span className="block text-gray-900 text-3xl md:text-4xl lg:text-5xl mt-1">
                Overview
              </span>
            </h1>
          </div>

          {/* Main Description - Reduced margin */}
          <div
            className={`max-w-5xl mx-auto mb-10 transform transition-all duration-1000 delay-400 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-6 md:p-8 border border-blue-200 shadow-lg">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-4">
                <strong className="text-gray-900">GQBS LAB</strong> is the central innovation hub of{' '}
                <strong className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  GLOCYBS
                </strong>
                , built to bring together the most advanced and integrated quantum-AI systems under one subscription.
              </p>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6">
                Designed for developers, enterprises, researchers, and visionaries, GQBS LAB provides seamless access to our complete ecosystem.
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-white rounded-2xl p-4 border border-blue-100 shadow-sm">
                  <h3 className="text-base font-bold text-gray-900 mb-2">
                    🚀 Future-Ready Ecosystem
                  </h3>
                  <p className="text-sm text-gray-600">
                    Every component is interconnected to enhance performance, security, and intelligence across your workflows.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-4 border border-purple-100 shadow-sm">
                  <h3 className="text-base font-bold text-gray-900 mb-2">
                    ⚡ Quantum Evolution
                  </h3>
                  <p className="text-sm text-gray-600">
                    With one subscription, you're not accessing products — you're unlocking the power of quantum evolution.
                  </p>
                </div>
              </div>

              <div className="text-center">
                <p className="text-lg font-semibold text-gray-800">
                  Whether you're building solutions, running simulations, conducting research, or enhancing security,{' '}
                  <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    GQBS LAB equips you with what's next
                  </span>{' '}
                  — today.
                </p>
              </div>
            </div>
          </div>

          {/* Products Section - Reduced margin */}
          <div
            className={`mb-10 transform transition-all duration-1000 delay-600 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-8 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Core Ecosystem Components
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              {products.map((product, index) => {
                const IconComponent = product.icon;
                return (
                  <div
                    key={product.name}
                    className={`group p-6 rounded-3xl bg-gradient-to-br ${
                      product.color === 'blue' ? 'from-blue-50 to-cyan-50 border-blue-300 hover:from-blue-100 hover:to-cyan-100' :
                      product.color === 'purple' ? 'from-purple-50 to-pink-50 border-purple-300 hover:from-purple-100 hover:to-pink-100' :
                      'from-cyan-50 to-blue-50 border-cyan-300 hover:from-cyan-100 hover:to-blue-100'
                    } border hover:scale-105 transition-all duration-500 cursor-pointer shadow-lg hover:shadow-xl`}
                    style={{ animationDelay: `${800 + index * 200}ms` }}
                  >
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-r ${
                      product.color === 'blue' ? 'from-blue-500 to-cyan-500' :
                      product.color === 'purple' ? 'from-purple-500 to-pink-500' :
                      'from-cyan-500 to-blue-500'
                    } flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>

                    <h3 className={`text-xl font-black mb-3 bg-gradient-to-r ${
                      product.color === 'blue' ? 'from-blue-600 to-cyan-600' :
                      product.color === 'purple' ? 'from-purple-600 to-pink-600' :
                      'from-cyan-600 to-blue-600'
                    } bg-clip-text text-transparent group-hover:scale-105 transition-transform`}>
                      {product.name}
                    </h3>

                    <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                      {product.description}
                    </p>

                    <div className="space-y-1">
                      {product.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${
                            product.color === 'blue' ? 'from-blue-500 to-cyan-500' :
                            product.color === 'purple' ? 'from-purple-500 to-pink-500' :
                            'from-cyan-500 to-blue-500'
                          }`} />
                          <span className="text-xs text-gray-600 font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Action Buttons Section - Reduced margin */}
          <div
            className={`mb-10 transform transition-all duration-1000 delay-800 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-8 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Access Your Tools
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {actionButtons.map((button, index) => {
                const IconComponent = button.icon;
                return (
                  <button
                    key={button.path}
                    onClick={() => handleNavigation(button.path)}
                    className={`group p-5 rounded-2xl bg-gradient-to-br ${
                      button.color === 'blue' ? 'from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600' :
                      button.color === 'purple' ? 'from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600' :
                      button.color === 'cyan' ? 'from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600' :
                      'from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600'
                    } text-white hover:scale-105 transform transition-all duration-300 shadow-lg hover:shadow-xl text-left`}
                    style={{ animationDelay: `${1000 + index * 150}ms` }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <IconComponent className="w-6 h-6 group-hover:scale-110 transition-transform" />
                      <ArrowRight className="w-4 h-4 opacity-70 group-hover:translate-x-1 group-hover:opacity-100 transition-all" />
                    </div>

                    <h3 className="text-base font-bold mb-1 group-hover:text-white transition-colors">
                      {button.title}
                    </h3>

                    <p className="text-xs opacity-90 group-hover:opacity-100 transition-opacity">
                      {button.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Call to Action - Reduced margin */}
          <div
            className={`text-center transform transition-all duration-1000 delay-1000 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-6 border border-blue-200 shadow-lg">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                Ready to Enter the Future?
              </h3>
              <p className="text-base text-gray-600 mb-6 max-w-2xl mx-auto">
                Join thousands of innovators already using GQBS LAB to build the next generation of quantum-powered applications.
              </p>
              <button className="group px-8 py-3 bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 text-white rounded-full font-bold text-base hover:scale-110 transform transition-all duration-300 shadow-xl hover:shadow-blue-500/50">
                <span className="flex items-center gap-2">
                  Start Building Now
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Animated Tech Elements */}
      <div
        className="absolute top-32 left-10 w-20 h-20 border-2 border-blue-300 rounded-2xl transform rotate-45 animate-spin opacity-20"
        style={{ animationDuration: '20s' }}
      />
      <div className="absolute bottom-32 right-10 w-16 h-16 border-2 border-purple-300 rounded-full animate-pulse opacity-30" />
      <div
        className="absolute top-1/3 right-20 w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full opacity-20 animate-bounce"
        style={{ animationDelay: '1s' }}
      />

      <style jsx>{`
        @keyframes meshMove {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
          }
          33% {
            transform: translate(30px, -20px) rotate(120deg);
          }
          66% {
            transform: translate(-20px, 30px) rotate(240deg);
          }
        }

        @keyframes gridPulse {
          0%, 100% {
            opacity: 0.05;
            transform: scale(1);
          }
          50% {
            opacity: 0.1;
            transform: scale(1.02);
          }
        }

        @keyframes particleFloat {
          0%, 100% {
            transform: translateY(0px) rotate(0deg) scale(1);
          }
          25% {
            transform: translateY(-20px) rotate(90deg) scale(1.1);
          }
          50% {
            transform: translateY(-10px) rotate(180deg) scale(0.9);
          }
          75% {
            transform: translateY(-15px) rotate(270deg) scale(1.05);
          }
        }
      `}</style>
    </div>
  );
};

export default OverviewPage;